import { BlobCtaButton, CardGlass, GlassChip, Icon, Skeleton, Typography } from "@cupcodev/ui";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "../app/i18n/I18nProvider";

/**
 * Nave page composition. Every visual primitive comes from @cupcodev/ui;
 * these helpers only arrange them so all routes share one layout grammar.
 */

export type ChipTone = "success" | "purple" | "destructive" | "warning" | "neutral";

export type Crumb = Readonly<{ label: string; to?: string | undefined }>;

export const NaveIcon = ({ name, className }: { name: string; className?: string | undefined }) => (
  <Icon icon={name} className={className ? `nave-icon ${className}` : "nave-icon"} aria-hidden="true" />
);

/** Navigation CTA built on the DS Blob button. Internal routes use the router;
 * external targets open in a new tab; same-origin downloads keep the page. */
export const CtaLink = ({ to, href, external = false, variant = "secondary", size = "sm", icon, trailingIcon, children }: {
  to?: string | undefined;
  href?: string | undefined;
  external?: boolean | undefined;
  variant?: "primary" | "secondary" | undefined;
  size?: "sm" | "md" | "lg" | undefined;
  icon?: string | undefined;
  trailingIcon?: string | undefined;
  children: ReactNode;
}) => {
  const navigate = useNavigate();
  const go = (): void => {
    if (to !== undefined) navigate(to);
    else if (href !== undefined) {
      if (external) window.open(href, "_blank", "noopener,noreferrer");
      else window.location.assign(href);
    }
  };
  return <BlobCtaButton type="button" variant={variant} size={size} onClick={go}>
    {icon ? <NaveIcon name={icon} /> : null}{children}{trailingIcon ? <NaveIcon name={trailingIcon} /> : null}
  </BlobCtaButton>;
};

export const Breadcrumbs = ({ items }: { items: readonly Crumb[] }) => {
  const { t } = useI18n();
  return (
    <nav className="nave-crumbs" aria-label={t("layout.breadcrumb")}>
      <ol>
        <li><Link to="/" aria-label={t("navigation.home")}><NaveIcon name="lucide:house" /></Link></li>
        {items.map((item, index) => (
          <li key={`${item.label}:${index}`}>
            <NaveIcon name="lucide:chevron-right" className="nave-crumbs-sep" />
            {item.to !== undefined && index < items.length - 1
              ? <Link to={item.to}>{item.label}</Link>
              : <span aria-current="page">{item.label}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export const PageHeader = ({
  title,
  description,
  crumbs,
  status,
  actions,
  meta,
  icon,
}: {
  title: ReactNode;
  description?: ReactNode | undefined;
  crumbs?: readonly Crumb[] | undefined;
  status?: Readonly<{ label: string; tone?: ChipTone | undefined }> | undefined;
  actions?: ReactNode | undefined;
  meta?: ReactNode | undefined;
  icon?: string | undefined;
}) => (
  <header className="nave-page-header">
    {crumbs !== undefined && crumbs.length > 0 ? <Breadcrumbs items={crumbs} /> : null}
    <div className="nave-page-header-row">
      <div className="nave-page-heading">
        {icon !== undefined ? <span className="nave-page-icon" aria-hidden="true"><NaveIcon name={icon} /></span> : null}
        <div className="nave-page-heading-copy">
          <div className="nave-page-title-line">
            <Typography as="h1" variant="display-sm" className="nave-page-title">{title}</Typography>
            {status !== undefined ? <GlassChip variant={status.tone ?? "neutral"}>{status.label}</GlassChip> : null}
          </div>
          {description !== undefined ? <Typography as="p" variant="body-md" tone="muted" className="nave-page-description">{description}</Typography> : null}
          {meta !== undefined ? <div className="nave-page-meta">{meta}</div> : null}
        </div>
      </div>
      {actions !== undefined ? <div className="nave-page-actions">{actions}</div> : null}
    </div>
  </header>
);

export const MetaItem = ({ icon, children }: { icon: string; children: ReactNode }) => (
  <span className="nave-meta-item"><NaveIcon name={icon} />{children}</span>
);

/** A titled glass surface. Count and action live on the heading row. */
export const Panel = ({
  title,
  icon,
  count,
  action,
  children,
  className,
  id,
  tone = "glass-md",
}: {
  title: string;
  icon?: string | undefined;
  count?: number | undefined;
  action?: ReactNode | undefined;
  children?: ReactNode | undefined;
  className?: string | undefined;
  id?: string | undefined;
  tone?: "glass-sm" | "glass-md" | undefined;
}) => {
  const headingId = id ?? `panel-${title.toLocaleLowerCase().replace(/[^a-z0-9]+/giu, "-")}`;
  return (
    <section className={className ? `nave-panel ${className}` : "nave-panel"} aria-labelledby={headingId}>
      <CardGlass variant={tone} elevation={0} className="nave-panel-card">
        <div className="nave-panel-head">
          <div className="nave-panel-title">
            {icon !== undefined ? <NaveIcon name={icon} /> : null}
            <Typography as="h2" id={headingId} variant="title-sm">{title}</Typography>
            {count !== undefined ? <span className="nave-count" aria-hidden="true">{count}</span> : null}
          </div>
          {action}
        </div>
        <div className="nave-panel-body">{children}</div>
      </CardGlass>
    </section>
  );
};

/** Whole-row navigation target: one tab stop, one obvious destination. */
export const RowLink = ({
  href,
  title,
  subtitle,
  leading,
  trailing,
  meta,
  external = false,
  download = false,
  onOpen,
}: {
  href: string;
  title: ReactNode;
  subtitle?: ReactNode | undefined;
  leading?: ReactNode | undefined;
  trailing?: ReactNode | undefined;
  meta?: ReactNode | undefined;
  external?: boolean | undefined;
  download?: boolean | undefined;
  /** Runs when the row is opened (click or Enter), without blocking the navigation. */
  onOpen?: (() => void) | undefined;
}) => {
  const content = (
    <>
      {leading !== undefined ? <span className="nave-row-leading">{leading}</span> : null}
      <span className="nave-row-main">
        <span className="nave-row-title">{title}</span>
        {subtitle !== undefined ? <span className="nave-row-subtitle">{subtitle}</span> : null}
        {meta !== undefined ? <span className="nave-row-meta">{meta}</span> : null}
      </span>
      {trailing !== undefined ? <span className="nave-row-trailing">{trailing}</span> : null}
      <span className="nave-row-go" aria-hidden="true"><NaveIcon name={external ? "lucide:arrow-up-right" : download ? "lucide:download" : "lucide:chevron-right"} className="nave-row-chevron" /></span>
    </>
  );
  if (external) return <a className="nave-row" href={href} target="_blank" rel="noopener noreferrer" onClick={onOpen}>{content}</a>;
  if (download) return <a className="nave-row" href={href} download onClick={onOpen}>{content}</a>;
  return <Link className="nave-row" to={href} onClick={onOpen}>{content}</Link>;
};

export const RowList = ({ children, label }: { children: ReactNode; label?: string | undefined }) => (
  <ul className="nave-row-list" aria-label={label}>{children}</ul>
);

export const RowIcon = ({ name, tone = "neutral" }: { name: string; tone?: "neutral" | "accent" | "alert" | "success" | undefined }) => (
  <span className={`nave-row-icon nave-row-icon-${tone}`}><NaveIcon name={name} /></span>
);

export const EmptyState = ({ icon = "lucide:inbox", title, children, action }: { icon?: string | undefined; title?: string | undefined; children: ReactNode; action?: ReactNode | undefined }) => (
  <div className="nave-empty">
    <span className="nave-empty-icon" aria-hidden="true"><NaveIcon name={icon} /></span>
    <div className="nave-empty-copy">
      {title !== undefined ? <Typography as="p" variant="title-sm">{title}</Typography> : null}
      <Typography as="p" variant="body-sm" tone="muted">{children}</Typography>
    </div>
    {action}
  </div>
);

/** Label/value facts rendered as a semantic description list. */
export const Facts = ({ items, columns = 1 }: { items: readonly (Readonly<{ label: string; value: ReactNode; icon?: string | undefined }> | null | false | undefined)[]; columns?: 1 | 2 | 3 | undefined }) => {
  const visible = items.filter((item): item is Readonly<{ label: string; value: ReactNode; icon?: string | undefined }> => Boolean(item));
  if (visible.length === 0) return null;
  return (
    <dl className={`nave-facts nave-facts-${columns}`}>
      {visible.map((item) => (
        <div key={item.label} className="nave-fact">
          <dt>{item.icon !== undefined ? <NaveIcon name={item.icon} /> : null}{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
};

export const PageSkeleton = ({ label, layout = "list" }: { label: string; layout?: "list" | "detail" | "grid" | undefined }) => (
  <div className={`nave-skeleton-page nave-skeleton-${layout}`} aria-busy="true" aria-live="polite">
    <span className="sr-only">{label}</span>
    <Skeleton variant="rounded" className="nave-skeleton-title" />
    <Skeleton variant="rounded" className="nave-skeleton-text" />
    <div className="nave-skeleton-body">
      <Skeleton variant="rounded" className="nave-skeleton-block" />
      <Skeleton variant="rounded" className="nave-skeleton-block" />
      <Skeleton variant="rounded" className="nave-skeleton-block" />
    </div>
  </div>
);

export const SectionSkeleton = ({ label, rows = 3 }: { label: string; rows?: number | undefined }) => (
  <div className="nave-skeleton-rows" aria-busy="true" aria-live="polite">
    <span className="sr-only">{label}</span>
    {Array.from({ length: rows }, (_, index) => <Skeleton key={index} variant="rounded" className="nave-skeleton-row" />)}
  </div>
);

/** Filter tabs for list pages. Purely presentational state owned by the page. */
export const FilterTabs = <T extends string,>({ value, options, onChange, label }: {
  value: T;
  options: readonly Readonly<{ value: NoInfer<T>; label: string; count?: number | undefined }>[];
  onChange(value: T): void;
  label: string;
}) => (
  <div className="nave-tabs" role="group" aria-label={label}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        aria-pressed={value === option.value}
        className="nave-tab"
        onClick={() => onChange(option.value)}
      >
        {option.label}
        {option.count !== undefined ? <span className="nave-tab-count">{option.count}</span> : null}
      </button>
    ))}
  </div>
);

export const Notice = ({ tone = "info", children, icon }: { tone?: "info" | "warning" | "success" | undefined; children: ReactNode; icon?: string | undefined }) => (
  <div className={`nave-notice nave-notice-${tone}`} role="status">
    <NaveIcon name={icon ?? (tone === "warning" ? "lucide:triangle-alert" : tone === "success" ? "lucide:circle-check" : "lucide:info")} />
    <span>{children}</span>
  </div>
);
